// src/components/business/BusinessOverviewTab.tsx
//
// Aba "Visão geral" da tela do negócio — réplica literal do wireframe
// seção 04 (#bp-vis): metabar (Tipo · Status · Repositórios · Demandas) +
// campos "Sobre" e "Público-alvo". Classes em src/styles/wf-biz.css.
//
// C4: contagens da metabar vêm do payload (business.repos_count/demands_count).

import type { Business, BusinessDNA } from '@/lib/api';
import { WfDot } from '@/components/wf';
import { LIFECYCLE_DOT_TONE, LIFECYCLE_LABELS } from './business-constants';

interface BusinessOverviewTabProps {
  business: Business;
  dna: BusinessDNA | null;
}

/**
 * "Sobre" do wireframe: descrição/explicação do negócio. Não existe coluna
 * canônica — lemos do jsonb `businesses.dna` (chaves description/about/sobre).
 */
function aboutFromBusiness(business: Business): string | null {
  const dna = business.dna ?? {};
  for (const key of ['description', 'about', 'sobre']) {
    const v = dna[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}

export function BusinessOverviewTab({ business, dna }: BusinessOverviewTabProps) {
  const about = aboutFromBusiness(business);

  return (
    <div className="wf-bizpanel">
      {/* Metabar (wireframe .metab/.meta/.mk/.mv) */}
      <div className="wf-metab">
        <div className="wf-meta">
          <div className="mk">Tipo</div>
          <div className="mv">{business.type}</div>
        </div>
        <div className="wf-meta">
          <div className="mk">Status</div>
          <div className="mv">
            <WfDot tone={LIFECYCLE_DOT_TONE[business.lifecycle_status] ?? 'neutral'} />
            {LIFECYCLE_LABELS[business.lifecycle_status] ?? business.lifecycle_status}
          </div>
        </div>
        <div className="wf-meta">
          <div className="mk">Repositórios</div>
          <div className="mv">{business.repos_count ?? '—'}</div>
        </div>
        <div className="wf-meta">
          <div className="mk">Demandas</div>
          <div className="mv">{business.demands_count ?? '—'}</div>
        </div>
      </div>

      {/* Sobre (wireframe .fld/.fk) */}
      <div className="wf-fld">
        <div className="fk">Sobre</div>
        <p>
          {about ?? <em style={{ color: 'var(--wf-muted)' }}>Sem descrição do negócio.</em>}
        </p>
      </div>

      {/* Público-alvo (DNA) */}
      <div className="wf-fld">
        <div className="fk">Público-alvo</div>
        <p>
          {dna?.audience?.trim() ? (
            dna.audience
          ) : (
            <em style={{ color: 'var(--wf-muted)' }}>Sem público-alvo definido no DNA.</em>
          )}
        </p>
      </div>
    </div>
  );
}
